import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { Dictionary } from "lodash";

import { getCompetencies } from "@/components/Funnel/utils/get-competencies";
import { getFips } from "@/components/Funnel/utils/get-fips";
import type {
  AvailableSlot,
  GetAvailableSlotsRequest,
  PracticeArea,
} from "@/edge/contract";
import { convertObjectToQueryString } from "@/edge/contract";
import { createAvailableSlotsRequestWithDefaults } from "@/edge/contract";
import { groupLssSlotsByDate } from "@/hooks/lss/lss.utils";
import { jsonRequest } from "@/lib/network/json-request";
import {
  isQualifiedInternalEmail,
  testCounty,
  testStateCode,
} from "@/modules/v2/funnel/visitor-checks/checkQualifiedInternalEmail";

type UseLssSlotsProps = Omit<
  UseQueryOptions<Dictionary<{ start: Date }[]>>,
  "queryKey" | "queryFn"
> & {
  userProps: {
    competencies?: string[];
    county?: string;
    email?: string;
    practiceArea: PracticeArea;
    serviceType?: string;
    sponsorType?: string;
    stateCode?: string;
    subPractice?: string;
  };
};

async function request(payload: GetAvailableSlotsRequest) {
  const res = await jsonRequest<AvailableSlot[]>(
    `/api/vinny/bookee/availability/LSS/slots?${convertObjectToQueryString(payload)}`,
  );

  if (!res) {
    throw new Error("No slots obtained");
  }

  return groupLssSlotsByDate(res);
}

export function useLssSlots({
  userProps: {
    practiceArea,
    subPractice,
    serviceType,
    sponsorType,
    stateCode: userStateCode,
    county: userCounty,
    email,
    competencies,
  },
  ...options
}: UseLssSlotsProps) {
  let localCompetencies = [];
  if (competencies && competencies.length > 0) {
    localCompetencies = competencies;
  } else {
    localCompetencies = getCompetencies({
      practice: practiceArea,
      subPractice,
      serviceType,
      sponsorType,
    });
  }

  const isValidFlareEmail = isQualifiedInternalEmail(email, "dlss");

  const [stateCode, county] = isValidFlareEmail
    ? [testStateCode, testCounty]
    : [userStateCode, userCounty];

  const fips = getFips({
    county,
    stateCode,
  });

  const availableSlotsPayload: GetAvailableSlotsRequest =
    createAvailableSlotsRequestWithDefaults({
      practiceArea,
      competencies,
      minSlots: 2,
    });

  if (practiceArea === "Family") {
    availableSlotsPayload.stateCode = stateCode;
    availableSlotsPayload.fips = fips;
  }

  const isValidSlotsRequest =
    practiceArea && !!fips && localCompetencies.length > 0;

  return useQuery<Dictionary<{ start: Date }[]>>({
    ...options,
    queryKey: [availableSlotsPayload],
    queryFn: () => request(availableSlotsPayload),
    enabled: Boolean(isValidSlotsRequest && (options.enabled ?? true)),
  });
}
